// Component: ResumeTradingModal.jsx
// Purpose: #133 — the ONE resume flow for the user-scope kill-switch, opened from the app-wide
//          banner and from the strategy-header chip. Shows what tripped it, takes a reason
//          (audited verbatim), one primary action. No inline inputs in header rows
//          (Deepak, 2026-08-28: "the UI is a mess … chaotic … not a good user experience,
//          especially when we are trying to unblock").

import React, { useEffect, useState } from 'react';
import { Modal, Button } from '../ds';
import { KILL_SWITCH_DEFAULT_REASON, fmtIstTime } from '../../hooks/trading/useOwnKillSwitch';

//--> The R57 cascade writes a machine string into the audit row:
//    "bracket_attach_cascade_exhausted:order=<uuid>:sii=<user>:<strategy>:<version>:<instrument>:<tf>".
//    Turn it into a sentence; keep the raw text behind "Technical details".
const TRIP_LABELS = {
  bracket_attach_cascade_exhausted:
    'A live entry filled but OMS could not attach its stop-loss / take-profit, so the position was unprotected and new entries were halted.',
  margin_shortfall: 'The broker reported a margin shortfall.',
  daily_loss_circuit_breaker: 'The daily loss limit was hit.',
};
const explainTrip = (reason) => {
  if (!reason) return { text: 'Reason not recorded.', order: null };
  const raw = String(reason);
  const head = raw.split(':')[0];
  const order = (raw.match(/order=([0-9a-f-]{36})/i) || [])[1] || null;
  const text = TRIP_LABELS[head] || `Tripped by "${head.replace(/_/g, ' ')}".`;
  return { text, order };
};

export default function ResumeTradingModal({ open, onClose, trip, busy, error, setError, resume }) {
  /*>>> State <<<*/
  const [reason, setReason] = useState(KILL_SWITCH_DEFAULT_REASON);

  useEffect(() => {
    if (open) {
      setReason(KILL_SWITCH_DEFAULT_REASON);
      if (setError) setError(null);
    }
  }, [open, setError]);

  /*>>> Handlers <<<*/
  const explained = explainTrip(trip?.reason);

  const onConfirm = async () => {
    const ok = await resume(reason);
    if (ok) onClose();
  };

  return (
    <Modal
      open={open}
      onClose={() => !busy && onClose()}
      size="md"
      title="Resume trading?"
      footer={(
        <div className="flex items-center justify-end gap-2 w-full">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={onConfirm} disabled={busy}>
            {busy ? 'Resuming…' : 'Resume trading'}
          </Button>
        </div>
      )}
    >
      <div className="flex flex-col gap-3 text-sm">
        <p>
          Your account&apos;s kill-switch is <strong>ACTIVE</strong>: OMS refuses every new live entry
          until you resume. Resuming is an audited operator action — it does not re-protect or
          close any position by itself.
        </p>
        <div className="rounded border px-3 py-2 flex flex-col gap-1" style={{ borderColor: 'var(--divider)' }}>
          <div className="text-xs uppercase tracking-wide opacity-70">What happened</div>
          <div>
            {explained.text}
            {explained.order ? <> Entry order <code>{explained.order.slice(0, 8)}</code>.</> : null}
          </div>
          <div className="text-xs uppercase tracking-wide opacity-70 mt-1">When</div>
          <div>{trip ? `${fmtIstTime(trip.timestamp)} IST` : 'unknown'}</div>
          <details className="mt-1">
            <summary className="text-xs opacity-70 cursor-pointer">Technical details</summary>
            <div className="break-all text-xs font-mono opacity-80 mt-1">{trip?.reason || 'not recorded'}</div>
          </details>
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide opacity-70">Your reason (written to the audit log)</span>
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            className="w-full rounded border px-2 py-1"
            style={{ borderColor: 'var(--divider)', color: '#111' }}
          />
        </label>
        {error ? <div style={{ color: '#f87171' }}>{error}</div> : null}
      </div>
    </Modal>
  );
}
